import { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { Button } from './ui/button';
import { X, Loader2, Upload, FileUp, Check, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { uploadCatalogFile, getClassificationStatus } from '../services/api';
import type { CatalogColumnInfo, ClassificationStatusResponse } from '../services/api';
import { useAsyncMutation } from '../hooks/useAsyncMutation';
import type { AsyncMutationResponse } from '../hooks/useAsyncMutation';
import type { CatalogUploadResponse } from '../services/api';

interface CatalogUploadDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadComplete?: (catalogId: string) => void;
}

export function CatalogUploadDrawer({ open, onOpenChange, onUploadComplete }: CatalogUploadDrawerProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ message: string; isError: boolean } | null>(null);
  const [uploadedCatalog, setUploadedCatalog] = useState<{ id: string; item_count: number } | null>(null);
  const [columnInfo, setColumnInfo] = useState<CatalogColumnInfo | null>(null);
  const [classificationStatus, setClassificationStatus] = useState<ClassificationStatusResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const classificationPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleFileSelect = (file: File) => {
    const validTypes = [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const validExtensions = ['.csv', '.xlsx'];
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      setUploadResult({ message: 'Please upload a CSV or Excel file (.csv, .xlsx)', isError: true });
      return;
    }

    setSelectedFile(file);
    setUploadResult(null);
    setUploadedCatalog(null);
    setColumnInfo(null);
    stopClassificationPolling();
    setClassificationStatus(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  function stopClassificationPolling() {
    if (classificationPollRef.current) {
      clearInterval(classificationPollRef.current);
      classificationPollRef.current = null;
    }
  }

  // Reuses the same live-progress polling pattern as StartNewQuotePage's
  // startClassificationPolling and CatalogManagePage's classification poll
  // effect: hit GET /api/v1/catalogs/:id/classification_status on an
  // interval until status flips to "complete", instead of leaving the drawer
  // on a blind "Uploading..." spinner while categorization runs server-side.
  const startClassificationPolling = (catalogId: string, itemCount: number) => {
    stopClassificationPolling();
    classificationPollRef.current = setInterval(async () => {
      const statusRes = await getClassificationStatus(catalogId);
      if (statusRes.data) {
        setClassificationStatus(statusRes.data);
        if (statusRes.data.status === 'complete') {
          stopClassificationPolling();
          setUploadedCatalog({ id: catalogId, item_count: itemCount });
        }
      }
    }, 2000);
  };

  // Clean up polling on unmount so a closed/unmounted drawer doesn't keep
  // hitting the status endpoint.
  useEffect(() => {
    return () => stopClassificationPolling();
  }, []);

  // BUG: post-upload refresh routed through the shared useAsyncMutation hook
  // so onUploadComplete fires the instant the 200 lands, not on drawer
  // dismiss (handleClose used to be the only place that called it, so the
  // parent stayed stale until the user clicked "Done").
  const uploadMutation = useAsyncMutation(
    async (file: File): Promise<AsyncMutationResponse<CatalogUploadResponse>> => {
      const res = await uploadCatalogFile(file);
      if (res.error) {
        setColumnInfo(res.data?.column_info || null);
        setUploadResult({ message: res.error, isError: true });
      }
      // ApiResponse<T> (services/api.ts) has no string index signature, so it
      // is not structurally assignable to AsyncMutationResponse<T> as-is; the
      // two shapes are compatible at runtime (data?/error? plus extra
      // fields), so this cast just bridges the declared-type gap rather than
      // widening what the hook actually receives.
      return res as AsyncMutationResponse<CatalogUploadResponse>;
    },
    {
      onSuccess: (data) => {
        if (!data) return;
        const isZero = data.item_count === 0;
        setColumnInfo(data.column_info || null);
        setUploadResult({
          message: data.message || `${data.item_count} product${data.item_count === 1 ? '' : 's'} imported`,
          isError: isZero,
        });
        if (isZero) return;

        // Notify the parent (CatalogManagePage loadCatalog / DistributorHomePage
        // loadHome / CCTodayPage) right away so it is already refreshed
        // underneath by the time the user dismisses the "Done" screen.
        onUploadComplete?.(data.id);

        if (data.classification_status === 'pending') {
          setClassificationStatus({
            catalog_id: data.id,
            status: 'classifying',
            progress: 0,
            total: data.classification_total || 0,
            flagged_count: 0,
          });
          startClassificationPolling(data.id, data.item_count);
        } else {
          setUploadedCatalog({ id: data.id, item_count: data.item_count });
        }
      },
    }
  );

  const handleUpload = () => {
    if (!selectedFile) return;
    setUploadResult(null);
    uploadMutation.run(selectedFile);
  };

  const isClassifying = classificationStatus?.status === 'classifying';

  const handleClose = () => {
    stopClassificationPolling();
    // Reset state
    setSelectedFile(null);
    setUploadResult(null);
    setUploadedCatalog(null);
    setColumnInfo(null);
    setClassificationStatus(null);
    onOpenChange(false);
  };

  const estimatedRows = selectedFile
    ? `~${Math.round(selectedFile.size / 100)} rows (estimated)`
    : null;

  return (
    <Drawer open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }} direction="right">
      <DrawerContent className="w-full sm:max-w-xl h-full flex flex-col">
        <DrawerHeader className="border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DrawerTitle className="text-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Upload Catalog
              </DrawerTitle>
              <DrawerDescription className="text-sm mt-1">
                Import your product catalog as CSV or Excel
              </DrawerDescription>
            </div>
            <DrawerClose asChild>
              <button className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
              isDragging
                ? 'border-[#A5CFDD] bg-[#A5CFDD]/10'
                : selectedFile
                ? 'border-green-300 bg-green-50'
                : 'border-[#A5CFDD]/50 hover:border-[#A5CFDD] bg-gray-50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelect(file);
              }}
            />

            {selectedFile ? (
              <div className="space-y-2">
                <FileUp className="w-10 h-10 text-green-500 mx-auto" />
                <p className="text-sm font-medium text-[#2A2A2A]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-400">
                  {(selectedFile.size / 1024).toFixed(1)} KB {estimatedRows ? `· ${estimatedRows}` : ''}
                </p>
                <button
                  className="text-xs text-[#A5CFDD] hover:underline mt-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setUploadResult(null);
                    setUploadedCatalog(null);
                    setColumnInfo(null);
                    stopClassificationPolling();
                    setClassificationStatus(null);
                  }}
                >
                  Choose a different file
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <Upload className="w-10 h-10 text-[#A5CFDD] mx-auto" />
                <div>
                  <p className="text-sm font-medium text-[#2A2A2A]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    Drag and drop your catalog file here
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    or <span className="text-[#A5CFDD] hover:underline">browse</span> to select
                  </p>
                </div>
                <p className="text-xs text-gray-400">
                  Supports CSV, XLSX
                </p>
              </div>
            )}
          </div>

          {/* Upload result */}
          {uploadResult && (
            <div className={`rounded-lg p-4 flex flex-col gap-2 ${
              uploadResult.isError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
            }`}>
              <div className="flex items-start gap-3">
                {uploadResult.isError ? (
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <p className="text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {uploadResult.message}
                </p>
              </div>
            </div>
          )}

          {/* Column mapping results / Required columns info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-[#2A2A2A] mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {columnInfo ? 'Column Mapping' : 'Required Columns'}
            </h3>
            {columnInfo ? (
              <>
                {columnInfo.missing_required.length > 0 && (
                  <div className="mb-3 p-2.5 rounded-md bg-amber-50 border border-amber-200">
                    <p className="text-xs font-medium text-amber-700 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Missing required columns: {columnInfo.missing_required.map(f => {
                        const labels: Record<string, string> = { item_number: 'Item Number', product_name: 'Product Name', brand: 'Brand', pack_size: 'Pack Size' };
                        return labels[f] || f;
                      }).join(', ')}
                    </p>
                    <p className="text-[10px] text-amber-600 mt-1">
                      Products may not display correctly without these columns.
                    </p>
                  </div>
                )}
                <div className="space-y-1.5">
                  {[
                    { key: 'item_number', label: 'Item Number', required: true },
                    { key: 'product_name', label: 'Product Name', required: true },
                    { key: 'brand', label: 'Brand', required: false },
                    { key: 'pack_size', label: 'Pack Size', required: false },
                    { key: 'category', label: 'Category', required: false },
                    { key: 'price_cents', label: 'Price', required: false },
                  ].map((col) => {
                    const isMapped = columnInfo.mapped_fields.includes(col.key);
                    const mappedHeader = Object.entries(columnInfo.column_mapping)
                      .find(([, v]) => v === col.key)?.[0];
                    return (
                      <div key={col.key} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          {isMapped ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          ) : (
                            <XCircle className={`w-3.5 h-3.5 flex-shrink-0 ${col.required ? 'text-red-400' : 'text-gray-300'}`} />
                          )}
                          <span className={`font-medium ${isMapped ? 'text-[#2A2A2A]' : col.required ? 'text-red-500' : 'text-gray-400'}`}>
                            {col.label}
                          </span>
                          {col.required && !isMapped && (
                            <span className="text-[10px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded font-medium">
                              Missing
                            </span>
                          )}
                        </div>
                        <span className="text-gray-400 text-[11px] truncate max-w-[180px]">
                          {isMapped ? `← "${mappedHeader}"` : '-'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Detected headers */}
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-gray-400 hover:text-gray-600">
                    File headers detected ({columnInfo.headers.length})
                  </summary>
                  <p className="mt-1.5 text-[11px] text-gray-500 break-all">
                    {columnInfo.headers.filter(Boolean).join(', ')}
                  </p>
                </details>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  {[
                    { field: 'Item Number', desc: 'SKU or product code', required: true },
                    { field: 'Product Name', desc: 'Product description', required: true },
                    { field: 'Brand', desc: 'Manufacturer or brand name', required: false },
                    { field: 'Pack Size', desc: 'e.g. 6/10#, 4/1 GAL', required: false },
                    { field: 'Category', desc: 'Product category', required: false },
                    { field: 'Price', desc: 'Unit price', required: false },
                  ].map((col) => (
                    <div key={col.field} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-[#2A2A2A] font-medium">{col.field}</span>
                        {col.required && (
                          <span className="text-[10px] bg-[#F2993D]/10 text-[#F2993D] px-1.5 py-0.5 rounded font-medium">
                            Required
                          </span>
                        )}
                      </div>
                      <span className="text-gray-400">{col.desc}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-3">
                  Column headers are auto-mapped, exact names not required
                </p>
              </>
            )}
          </div>
        </div>

        <DrawerFooter className="border-t border-gray-200 flex-shrink-0">
          {uploadedCatalog ? (
            <Button
              onClick={handleClose}
              className="w-full bg-[#F2993D] hover:bg-[#E08A2E] text-white"
            >
              <Check className="w-4 h-4 mr-2" />
              Done: {uploadedCatalog.item_count} Product{uploadedCatalog.item_count === 1 ? '' : 's'} Imported
            </Button>
          ) : (
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploadMutation.loading || isClassifying}
              className="w-full bg-[#F2993D] hover:bg-[#E08A2E] text-white disabled:bg-gray-200 disabled:text-gray-400"
            >
              {uploadMutation.loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : isClassifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Classifying {classificationStatus?.progress ?? 0}/{classificationStatus?.total ?? 0}
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Catalog
                </>
              )}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
